{
  "blocks": [
    {
      "id": "moon-article-ivf-vs-iui",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "IVF vs. IUI: which is right for you?",
      "subtitle": "Breaking down the two most common fertility treatments.",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Crossroads",
        "quickSummary": [
          {
            "title": "IUI is simpler and cheaper.",
            "description": "IUI is less invasive and less expensive, but has lower per-cycle success rates (10–20%)."
          },
          {
            "title": "IVF offers higher success.",
            "description": "IVF is more involved but offers 40–60% success rates per transfer and allows genetic testing."
          },
          {
            "title": "Many start with IUI first.",
            "description": "Many patients start with IUI and move to IVF. Your doctor helps decide based on diagnosis, age, and time."
          }
        ],
        "sections": [
          {
            "title": "When IUI makes sense",
            "content": "IUI (intrauterine insemination) is often the first-line treatment for several common diagnoses. It's a less invasive procedure where washed, concentrated sperm is placed directly into the uterus around the time of ovulation.",
            "bulletPoints": [
              "Mild male factor infertility (slightly low count or motility)",
              "Unexplained infertility as a first step",
              "Ovulation disorders like PMOS (ex PCOS) (combined with medication)",
              "Single parents or same-sex couples using donor sperm"
            ]
          },
          {
            "title": "When IVF is recommended",
            "content": "IVF bypasses many of the barriers that can prevent natural conception or IUI success. Your doctor may recommend going directly to IVF in certain situations where IUI is unlikely to work.",
            "bulletPoints": [
              "Blocked or damaged fallopian tubes",
              "Severe male factor infertility",
              "Advanced maternal age (typically 38+)",
              "Diminished ovarian reserve (low AMH or AFC)",
              "Moderate to severe endometriosis",
              "Multiple failed IUI cycles (typically 3–4)",
              "Need for preimplantation genetic testing (PGT)"
            ]
          },
          {
            "title": "Comparing the processes side by side",
            "content": "IUI is a relatively quick office procedure: monitoring takes a few visits, and the insemination itself takes about 10 minutes with no anesthesia. IVF involves 10–14 days of daily injections, frequent monitoring visits, egg retrieval under sedation, embryo culture in a lab, and then a transfer procedure. The time commitment and physical demands are significantly different."
          },
          {
            "title": "Cost comparison",
            "content": "The financial difference between the two treatments is substantial, and cost is often a major factor in decision-making.",
            "bulletPoints": [
              "IUI: $500–$2,000 per cycle (plus $500–$1,500 for medications if using ovulation induction)",
              "IVF: $15,000–$25,000 per cycle (including medications, monitoring, retrieval, and transfer)",
              "After 3–4 failed IUI cycles, the cumulative cost can approach that of a single IVF cycle"
            ]
          },
          {
            "title": "Success rates by age and diagnosis",
            "content": "IUI success rates range from 10–20% per cycle depending on age and diagnosis. IVF success rates are significantly higher at 40–60% per transfer for patients under 35, declining with age. For patients over 40, IUI success rates drop below 5% per cycle, making IVF the more efficient path. Your specific diagnosis also matters: unexplained infertility may respond well to IUI initially, while tubal factor infertility requires IVF."
          },
          {
            "title": "The emotional factor in choosing",
            "content": "The decision between IUI and IVF isn't purely medical or financial. It's emotional. IUI feels less intense, and many patients prefer to start there to ease into treatment. IVF can feel overwhelming but may save time and emotional energy in the long run if IUI is unlikely to work. There's no wrong answer: the best choice is the one that aligns with your medical reality, financial situation, and emotional readiness."
          }
        ]
      }
    },
    {
      "id": "moon-article-when-to-change-clinics",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "When to change clinics",
      "subtitle": "Signs it might be time, and how to make the switch.",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Difficult choices",
        "quickSummary": [
          {
            "title": "It's more common than you think.",
            "description": "Changing clinics is more common than people think, and it's not disloyal."
          },
          {
            "title": "Watch for red flags.",
            "description": "Red flags include poor communication, cookie-cutter protocols, and dismissal of your concerns."
          },
          {
            "title": "Your records belong to you.",
            "description": "Your records belong to you. Transferring is straightforward."
          }
        ],
        "sections": [
          {
            "title": "Valid reasons to consider switching",
            "content": "There are several legitimate reasons patients consider changing clinics. Recognizing them can help you distinguish between normal treatment frustrations and genuine problems with your care.",
            "bulletPoints": [
              "Communication breakdown: you can't reach your care team or feel ignored",
              "Lack of protocol adjustments after a failed cycle",
              "Clinic location or scheduling is creating undue stress",
              "You want a second opinion on your treatment plan",
              "The clinic doesn't offer services you need (PGT, donor program, etc.)"
            ]
          },
          {
            "title": "Red flags vs. normal frustrations",
            "content": "Fertility treatment is inherently difficult, and not every frustration means you need a new clinic. A failed cycle isn't automatically the clinic's fault: even perfect protocols don't guarantee success. However, a clinic that never adjusts its approach, dismisses your questions, or makes you feel like a number rather than a patient is a different story. The key distinction is whether the clinic is responsive, transparent, and willing to evolve your care plan."
          },
          {
            "title": "How to get a second opinion without fully switching",
            "content": "You don't have to commit to leaving before exploring other options. Many patients seek second opinions while continuing with their current clinic. Most reproductive endocrinologists are used to this and won't take offense. A second opinion consultation typically costs $200–$500 and gives you a fresh perspective on your diagnosis and treatment plan."
          },
          {
            "title": "How to actually transfer",
            "content": "If you decide to switch, the logistics are simpler than most people expect. The process is well-established and clinics handle transfers regularly.",
            "bulletPoints": [
              "Request your complete medical records (they belong to you by law)",
              "If you have frozen embryos or eggs, arrange transport through a specialized cryoshipping company",
              "Embryo/egg transport typically costs $500–$1,500 and is handled by professionals",
              "Allow 2–4 weeks for records transfer and new patient onboarding",
              "Your new clinic will review everything before recommending a plan"
            ]
          },
          {
            "title": "What to ask a new clinic",
            "content": "When you consult with a new clinic, come prepared with your full history. Share what protocols were used, how you responded, and what the outcomes were. Ask the new doctor what they would do differently and why. Pay attention to whether they genuinely engage with your history or simply default to their standard approach.",
            "bulletPoints": [
              "What would you change about my previous protocol?",
              "How do you handle communication and follow-up?",
              "What's your approach when a cycle doesn't work?",
              "Can I see your SART data for patients in my age group and diagnosis?"
            ]
          },
          {
            "title": "Emotional aspects of the decision",
            "content": "Leaving a clinic can feel surprisingly emotional. You may feel guilt, loyalty to your doctor, or fear that starting over will set you back. These feelings are normal. Remember that advocating for yourself is not disloyalty. It's good patient care. Your goal is a baby, not a relationship with a clinic. If your gut is telling you something isn't right, trust it."
          }
        ]
      }
    },
    {
      "id": "moon-article-fresh-vs-frozen-choice",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "Fresh vs. frozen transfer: making the choice",
      "subtitle": "When to transfer immediately and when freezing first is smarter.",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Strategy",
        "quickSummary": [
          {
            "title": "Frozen now matches or exceeds fresh.",
            "description": "Frozen transfers now match or exceed fresh transfer success rates in most scenarios."
          },
          {
            "title": "Freeze-all has clear indications.",
            "description": "Freeze-all is preferred when doing PGT-A, if there's OHSS risk, or when hormones are elevated from stimulation."
          },
          {
            "title": "Fresh still has a place.",
            "description": "Fresh transfers save time and cost when conditions are right. It's not always better to freeze."
          }
        ],
        "sections": [
          {
            "title": "Why frozen caught up",
            "content": "The vitrification revolution changed everything. Older slow-freeze methods damaged cells through ice crystal formation, giving fresh transfers a clear advantage. Modern vitrification (flash-freezing at ultra-low temperatures) achieves 95%+ embryo survival rates. Combined with growing evidence that the uterine environment after ovarian stimulation is suboptimal for implantation, frozen transfers have become the standard at many clinics.",
            "bulletPoints": [
              "Frozen embryo survival: over 95% at experienced clinics",
              "Frozen oocyte survival: 90–97% with vitrification",
              "Older slow-freeze method: only 60–70% survival"
            ]
          },
          {
            "title": "When freeze-all makes sense",
            "content": "There are several situations where freezing all embryos and transferring in a subsequent cycle is clearly the better strategy.",
            "bulletPoints": [
              "PGT-A testing: results take 1–2 weeks, so embryos must be frozen while awaiting biopsy results",
              "Elevated progesterone during stimulation, which can impair endometrial receptivity",
              "Risk of ovarian hyperstimulation syndrome (OHSS): pregnancy worsens OHSS",
              "Scheduling flexibility: transfer on your timeline rather than the cycle's timeline",
              "Batch cycles: accumulating embryos from multiple retrievals before selecting the best for transfer"
            ]
          },
          {
            "title": "When fresh transfer makes sense",
            "content": "Despite the trend toward freeze-all, fresh transfers remain a good option for many patients. If you responded normally to stimulation, aren't doing PGT, and have a limited number of embryos, a fresh transfer avoids the added cost and waiting time of a frozen cycle. Some patients also prefer the emotional momentum of transferring in the same cycle rather than waiting another month or more."
          },
          {
            "title": "Success rate data comparison",
            "content": "Large studies show that frozen and fresh transfers produce comparable pregnancy rates for normal responders. For high responders (many eggs retrieved) and PMOS (ex PCOS) patients, frozen transfers show a statistically significant advantage. For low responders with few embryos, some data suggests fresh may be slightly better: the fewer embryos you have, the more each freeze-thaw cycle matters. Your doctor should tailor the recommendation to your specific situation."
          },
          {
            "title": "Cost considerations",
            "content": "A frozen embryo transfer (FET) adds approximately $3,000–$6,000 to your total treatment cost. This includes the freezing process, storage, medications for endometrial preparation, monitoring, and the transfer procedure itself. If you're already freezing embryos for PGT testing, the incremental cost of FET is built in. But if cost is a major factor and conditions favor a fresh transfer, going fresh can save a meaningful amount."
          },
          {
            "title": "The 'freeze everything' trend: going too far?",
            "content": "Some clinics now default to freeze-all for nearly every patient, regardless of circumstances. While this simplifies scheduling and may marginally improve average outcomes, it's not always in the individual patient's best interest. A blanket freeze-all policy can add unnecessary cost and delay for patients who would do just as well with a fresh transfer. Ask your clinic why they're recommending freeze-all for your specific case, not just their general policy."
          },
          {
            "title": "The waiting factor",
            "content": "One thing to consider on the patient side: FET adds a cycle of waiting. After retrieval, you'll typically wait at least one full menstrual cycle before transfer. For some, that delay is emotionally hard. The urge to transfer immediately can be intense. For others, it's a welcome pause after the intensity of stimulation, giving body and mind time to recover before the implantation window. Neither reaction is more 'correct' than the other."
          }
        ]
      }
    },
    {
      "id": "moon-article-after-failed-cycle",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "After a failed cycle: what to ask your doctor",
      "subtitle": "A failed cycle isn't the end. It's information. Here's how to use it.",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Next steps",
        "quickSummary": [
          {
            "title": "A failed cycle is data.",
            "description": "A failed cycle provides data that can improve the next attempt."
          },
          {
            "title": "Ask what they'd change.",
            "description": "Ask specific questions about what they'd change: protocol, timing, additional testing."
          },
          {
            "title": "It's okay to pause.",
            "description": "It's okay to take a break, seek a second opinion, or ask for more investigation."
          }
        ],
        "sections": [
          {
            "title": "Processing the disappointment first",
            "content": "Before diving into next steps, give yourself space to feel the loss. A failed cycle is a real grief: the loss of hope, time, money, and physical effort. There's no right timeline for processing this. Some people are ready to talk strategy the next day; others need weeks. Both responses are completely valid. Don't let anyone rush you into the next cycle before you're emotionally ready."
          },
          {
            "title": "Questions to ask about what happened",
            "content": "When you're ready, schedule a follow-up consultation with your doctor to review the cycle in detail. Understanding why it failed helps shape what comes next.",
            "bulletPoints": [
              "Why do you think this cycle didn't work?",
              "Was it likely an embryo quality issue or an implantation issue?",
              "How did my response to stimulation compare to what you expected?",
              "Were there any surprises during the cycle that concerned you?",
              "If we had embryos tested, what did the genetics show?"
            ]
          },
          {
            "title": "Protocol changes to discuss",
            "content": "A good RE will proactively suggest adjustments for the next cycle based on what they learned. If they don't, ask directly what they'd change.",
            "bulletPoints": [
              "Different medication dosing: higher or lower based on your response",
              "Adding growth hormone (HGH) to potentially improve egg quality",
              "Changing the trigger type: switching between hCG and Lupron trigger",
              "Adjusting stimulation length or adding a priming protocol",
              "Changing the transfer approach: fresh vs. frozen, or different endometrial preparation"
            ]
          },
          {
            "title": "Additional testing to consider",
            "content": "A failed cycle sometimes reveals the need for deeper investigation. Several tests can uncover issues that weren't apparent before treatment began.",
            "bulletPoints": [
              "ERA (endometrial receptivity analysis): checks if your implantation window timing is off",
              "Hysteroscopy: direct visualization of the uterine cavity for polyps, fibroids, or scarring",
              "Thrombophilia panel: tests for blood clotting disorders that can affect implantation",
              "Sperm DNA fragmentation: standard semen analysis doesn't measure DNA integrity",
              "Immunological testing: still controversial but increasingly explored"
            ]
          },
          {
            "title": "When to try again vs. when to investigate more",
            "content": "After a first failed cycle with a reasonable explanation (like poor embryo quality due to chance), most doctors recommend trying again with protocol adjustments. After two or more failed cycles (especially with good-quality embryos), it's worth investigating more aggressively before another attempt. The balance between trying again and testing more depends on your age, remaining embryos, financial situation, and emotional reserves."
          },
          {
            "title": "Considering a second opinion",
            "content": "There's no shame in seeking a second opinion after a failed cycle. In fact, it's one of the most common reasons patients consult with another RE. A fresh set of eyes may spot something your current doctor missed, suggest a different approach, or simply confirm that you're on the right track. You can get a second opinion without switching clinics entirely."
          },
          {
            "title": "The emotional weight of 'try again'",
            "content": "Deciding to do another cycle is not just a medical decision. It's an emotional, financial, and physical one. It's okay to take a cycle off. It's okay to set a limit on how many cycles you'll do. It's okay to change direction entirely. What's important is that the decision feels right for you, not that it follows some prescribed playbook. Your mental health matters as much as your protocol."
          }
        ]
      }
    }
  ],
  "relationships": [],
  "localeVariants": []
}
